// Statistics overview for the logged-in client — the same numbers the
// dashboard's Monitoring stats show for this client (GET /call-stats, which the
// API locks to the client). Cards, calls timeline, dispositions, and tables per
// bot (SIP), avatar and voice set. Bots that took no calls are listed too.
//
//   from, to — YYYY-MM-DD range from the page's date picker
import { useState, useEffect, useMemo } from 'react'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { Phone, PhoneForwarded, Voicemail, PhoneOff, Clock, Bot, ChevronUp, ChevronDown, Filter } from 'lucide-react'
import { useClientAuth } from '../../context/ClientAuthContext'
import StatCards from '../StatCards'
import MultiPicker from '../MultiPicker'

const DISPOSITIONS = [
  { key: 'transfer-call', label: 'Transfer', color: '#22c55e' },
  { key: 'not-interested', label: 'Not Interested', color: '#f59e0b' },
  { key: 'no-answer', label: 'No Answer', color: '#3b82f6' },
  { key: 'answering-machine', label: 'Voicemail', color: '#6b7280' },
  { key: 'age-do-not-qualify', label: 'Age Do Not Qualify', color: '#ef4444' },
  { key: 'do-not-call', label: 'DNC', color: '#dc2626' },
  { key: 'call-back', label: 'Call Back', color: '#06b6d4' },
  { key: 'busy', label: 'Busy', color: '#a78bfa' },
  { key: 'in-progress', label: 'In Progress', color: '#38bdf8' },
  { key: 'unknown', label: 'Unknown', color: '#94a3b8' }
]

const fmtNum = (n) => (n || 0).toLocaleString()
const fmtDur = (s) => { s = Math.round(s || 0); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
const pct = (n, total) => (total ? ((n / total) * 100).toFixed(1) : '0.0')
const rateClass = (r) => (r >= 5 ? 'tw-text-green-500' : r >= 2 ? 'tw-text-amber-500' : 'tw-text-red-500')
function fmtWhen(s) {
  if (!s) return '—'
  const d = new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : String(s).replace(' ', 'T') + 'Z')
  return isNaN(d) ? s : d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function ClientCallStats({ from, to }) {
  const { clientApi } = useClientAuth()
  const [bots, setBots] = useState([])
  const [sips, setSips] = useState([])          // SIP filter; [] = all bots
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    clientApi('/bots').then(d => setBots(d.bots || d.data || [])).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false
    const qs = new URLSearchParams({ from, to })
    if (sips.length) qs.set('sip_user', sips.join(','))
    setLoading(true); setErr(null)
    clientApi(`/call-stats?${qs}`)
      .then(d => { if (!cancelled) { if (!d.success) throw new Error(d.error || 'Failed to load statistics'); setData(d) } })
      .catch(e => { if (!cancelled) setErr(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, sips.join(',')])

  const sipOptions = useMemo(() => bots.filter(b => b.sip_user)
    .map(b => ({ value: String(b.sip_user), label: String(b.sip_user), sub: b.avatar_name || '' }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })), [bots])

  // Every bot in scope with its numbers (0 when it took no calls).
  const sipRows = useMemo(() => {
    const scope = sips.length ? new Set(sips) : null
    const rows = (data?.by_sip || []).filter(r => r.sip_user)
    const seen = new Set(rows.map(r => r.sip_user))
    for (const b of bots) {
      const s = String(b.sip_user || '')
      if (s && !seen.has(s) && (!scope || scope.has(s))) {
        rows.push({ sip_user: s, total: 0, transfers: 0, voicemail: 0, not_interested: 0, avg_duration: 0, transfer_rate: 0, last_call_at: null })
      }
    }
    return rows
  }, [data, bots, sips])

  const filterBar = (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <MultiPicker label="Bots" icon={Bot} options={sipOptions} selected={sips} onChange={setSips} groupInput allLabel="All" />
      {sips.length > 0 && (
        <button className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-text-primary hover:tw-underline" onClick={() => setSips([])}>
          Show all bots
        </button>
      )}
    </div>
  )

  if (loading && !data) return <div className="tw-flex tw-flex-col tw-gap-4">{filterBar}<div className="tw-p-6 tw-text-sm tw-text-muted">Loading statistics…</div></div>
  if (err) return <div className="tw-flex tw-flex-col tw-gap-4">{filterBar}<div className="tw-p-6 tw-text-sm tw-text-red-500">{err}</div></div>
  if (!data) return null

  const t = data.totals
  const silentBots = sipRows.filter(r => r.total === 0).length
  const dispoRows = DISPOSITIONS.map(d => ({ ...d, count: data.disposition_counts[d.key] || 0 })).filter(r => r.count > 0).sort((a, b) => b.count - a.count)
  const maxDispo = Math.max(1, ...dispoRows.map(r => r.count))
  const timeline = data.timeline.points.map(p => {
    const d = new Date(data.timeline.unit === 'hour' ? p.bucket : `${p.bucket}T00:00:00`)
    return {
      ...p,
      label: data.timeline.unit === 'hour'
        ? d.toLocaleTimeString(undefined, { hour: 'numeric' })
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    }
  })

  return (
    <div className={`tw-flex tw-flex-col tw-gap-4 ${loading ? 'tw-opacity-60' : ''}`}>
      {filterBar}

      <StatCards cards={[
        { key: 'calls', label: 'Total calls', value: fmtNum(t.total), icon: Phone, color: '#3b82f6', sub: `${fmtNum(Math.round((t.talk_time || 0) / 3600))} h talk time` },
        { key: 'xfer', label: 'Transfers', value: fmtNum(t.transfers), icon: PhoneForwarded, color: '#22c55e', sub: `${t.transfer_rate}% transfer rate` },
        { key: 'vm', label: 'Voicemail', value: fmtNum(t.voicemail), icon: Voicemail, color: '#6b7280', sub: `${pct(t.voicemail, t.total)}% of calls` },
        { key: 'ni', label: 'Not interested', value: fmtNum(t.not_interested), icon: PhoneOff, color: '#f59e0b', sub: `${pct(t.not_interested, t.total)}% of calls` },
        { key: 'dur', label: 'Avg duration', value: fmtDur(t.avg_duration), icon: Clock, color: '#8b5cf6', sub: `${t.avg_steps} steps on average` },
        {
          key: 'bots', label: 'Bots calling', value: fmtNum(sipRows.length - silentBots), icon: Bot, color: silentBots ? '#ef4444' : '#06b6d4',
          sub: silentBots ? `${silentBots} of ${sipRows.length} took no calls` : `all ${sipRows.length} took calls`
        }
      ]} />

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-[2fr_1fr]">
        <Card title={`Calls per ${data.timeline.unit}`}>
          {timeline.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={timeline} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total" name="Calls" fill="var(--primary)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="transfers" name="Transfers" fill="#22c55e" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Dispositions">
          {dispoRows.length === 0 ? <Empty /> : dispoRows.map(r => (
            <div key={r.key} className="tw-mb-2.5">
              <div className="tw-mb-1 tw-flex tw-justify-between tw-text-xs">
                <span className="tw-font-medium" style={{ color: r.color }}>{r.label}</span>
                <span className="tw-tabular-nums tw-text-muted">{fmtNum(r.count)} · {pct(r.count, t.total)}%</span>
              </div>
              <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-input">
                <div className="tw-h-full tw-rounded-full" style={{ width: `${(r.count / maxDispo) * 100}%`, background: r.color }} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      <SipTable rows={sipRows} onFilterSips={setSips} />

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 xl:tw-grid-cols-2">
        <StatsTable title="Avatars" rows={data.by_avatar} rowKey={r => r.avatar_name ?? 'none'}
          columns={[{ key: 'avatar_name', label: 'Avatar', render: r => r.avatar_name || '—', strong: true }, ...METRIC_COLS.slice(0, 3), METRIC_COLS[5]]} />
        <StatsTable title="Voice sets" rows={data.by_voice_set} rowKey={r => r.voice_set_name ?? 'none'}
          columns={[{ key: 'voice_set_name', label: 'Voice set', render: r => r.voice_set_name || '—', strong: true }, ...METRIC_COLS.slice(0, 3), METRIC_COLS[5]]} />
      </div>
    </div>
  )
}

const METRIC_COLS = [
  { key: 'total', label: 'Calls', num: true, render: r => fmtNum(r.total) },
  { key: 'transfers', label: 'Xfers', num: true, render: r => fmtNum(r.transfers) },
  { key: 'transfer_rate', label: 'Rate', num: true, render: r => <span className={`tw-font-semibold ${r.total ? rateClass(r.transfer_rate) : 'tw-text-muted'}`}>{r.transfer_rate}%</span> },
  { key: 'voicemail', label: 'Voicemail', num: true, render: r => fmtNum(r.voicemail) },
  { key: 'not_interested', label: 'Not int.', num: true, render: r => fmtNum(r.not_interested) },
  { key: 'avg_duration', label: 'Avg dur', num: true, render: r => fmtDur(r.avg_duration) },
  { key: 'last_call_at', label: 'Last call', render: r => <span className="tw-text-muted">{fmtWhen(r.last_call_at)}</span> }
]

function Card({ title, children, right }) {
  return (
    <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-border tw-bg-card tw-p-4">
      <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <h4 className="tw-m-0 tw-text-[13px] tw-font-semibold">{title}</h4>
        {right}
      </div>
      {children}
    </div>
  )
}
const Empty = () => <div className="tw-py-6 tw-text-center tw-text-xs tw-text-muted">No calls in this range.</div>

function useSort(rows, initial = 'total') {
  const [sort, setSort] = useState({ key: initial, dir: -1 })
  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key]
    if (x == null && y == null) return 0
    if (x == null) return 1
    if (y == null) return -1
    return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
  }), [rows, sort])
  const header = (c) => (
    <th key={c.key} onClick={() => setSort(s => ({ key: c.key, dir: s.key === c.key ? -s.dir : (c.num ? -1 : 1) }))}
      className={`tw-cursor-pointer tw-select-none tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-muted ${c.num ? 'tw-text-right' : 'tw-text-left'}`}>
      {c.label}
      {sort.key === c.key && (sort.dir < 0 ? <ChevronDown size={11} className="tw-ml-0.5 tw-inline" /> : <ChevronUp size={11} className="tw-ml-0.5 tw-inline" />)}
    </th>
  )
  return { sorted, header }
}

function cell(c, r) {
  const v = c.render ? c.render(r) : (r[c.key] ?? '—')
  return (
    <td key={c.key} className={`tw-whitespace-nowrap tw-px-3 tw-py-1.5 ${c.num ? 'tw-text-right tw-tabular-nums' : ''} ${c.mono ? 'tw-font-mono' : ''} ${c.strong ? 'tw-font-semibold' : ''}`}>{v}</td>
  )
}

function StatsTable({ title, rows, columns, rowKey }) {
  const [all, setAll] = useState(false)
  const { sorted, header } = useSort(rows)
  const shown = all ? sorted : sorted.slice(0, 8)
  return (
    <Card title={<>{title} <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted">{rows.length}</span></>}
      right={rows.length > 8 && (
        <button className="btn btn-secondary btn-sm" onClick={() => setAll(a => !a)}>{all ? 'Top 8' : `Show all ${rows.length}`}</button>
      )}>
      {rows.length === 0 ? <Empty /> : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse tw-text-xs">
            <thead><tr className="tw-border-0 tw-border-b tw-border-solid tw-border-border">{columns.map(header)}</tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={rowKey(r)} className="tw-border-0 tw-border-t tw-border-solid tw-border-border">{columns.map(c => cell(c, r))}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// Per-bot table; tick bots and "Filter to selected" to see just those.
function SipTable({ rows, onFilterSips }) {
  const [picked, setPicked] = useState(() => new Set())
  const [all, setAll] = useState(false)
  const { sorted, header } = useSort(rows)
  const shown = all ? sorted : sorted.slice(0, 25)
  const toggle = (s) => setPicked(p => { const n = new Set(p); n.has(s) ? n.delete(s) : n.add(s); return n })
  const columns = [{ key: 'sip_user', label: 'Bot (SIP)', mono: true, strong: true }, ...METRIC_COLS]
  return (
    <Card title={<>Bots <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-muted">{rows.length}</span></>}
      right={<div className="tw-flex tw-items-center tw-gap-2">
        {picked.size > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => { onFilterSips([...picked]); setPicked(new Set()) }}>
            <Filter size={12} /> Filter to {picked.size} selected
          </button>
        )}
        {rows.length > 25 && <button className="btn btn-secondary btn-sm" onClick={() => setAll(a => !a)}>{all ? 'Top 25' : `Show all ${rows.length}`}</button>}
      </div>}>
      {rows.length === 0 ? <Empty /> : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse tw-text-xs">
            <thead><tr className="tw-border-0 tw-border-b tw-border-solid tw-border-border">
              <th className="tw-w-8 tw-px-3 tw-py-2" />
              {columns.map(header)}
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.sip_user} onClick={() => toggle(r.sip_user)}
                  className={`tw-cursor-pointer tw-border-0 tw-border-t tw-border-solid tw-border-border hover:tw-bg-card-hover ${r.total === 0 ? 'tw-opacity-60' : ''}`}>
                  <td className="tw-px-3 tw-py-1.5"><input type="checkbox" readOnly checked={picked.has(r.sip_user)} /></td>
                  {columns.map(c => cell(c, r))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
